<script>
    export let organization;
    /** [{ key, label }] in display order */
    export let steps = [];
    /** key of the section currently open, or null */
    export let currentKey = null;
    /** { [key]: boolean } */
    export let completed = {};
    export let isResearch = false;

    $: currentIndex = steps.findIndex(s => s.key === currentKey);
    $: activeIndex = currentIndex > -1 ? currentIndex : steps.length - 1;
    $: currentStep = steps[activeIndex];
</script>

<div class="checkout-page">
    <header class="checkout-header">
        <img class="checkout-logo" src={organization.branding_logo} alt={organization.nme} />
        <div class="header-marks">
            {#if isResearch}
            <span class="verified-mark">
                <i class="fa-solid fa-shield-check" aria-hidden="true"></i>
                Verified research study link
            </span>
            {/if}
            <span class="secure-mark">
                <i class="fa-solid fa-lock" aria-hidden="true"></i>
                Secure checkout
            </span>
        </div>
    </header>

    <slot name="banner" />

    {#if steps.length > 1}
    <div class="checkout-progress">
        <span class="progress-label">
            Step {activeIndex + 1} of {steps.length}{#if currentStep} &middot; {currentStep.label}{/if}
        </span>
        <div class="progress-bars">
            {#each steps as step, i}
                <span
                    class="progress-bar"
                    class:done={completed[step.key]}
                    class:active={!completed[step.key] && i === activeIndex}
                ></span>
            {/each}
        </div>
    </div>
    {/if}

    <div class="checkout-body">
        <div class="checkout-main">
            <slot />
        </div>
        <aside class="checkout-aside">
            <div class="checkout-aside-inner">
                <slot name="summary" />
            </div>
        </aside>
    </div>

    <footer class="checkout-footer">
        <slot name="footer" />
    </footer>
</div>

<style lang="scss">
    .checkout-page {
        font-family: Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        width: 100%;
        max-width: 1100px;
        margin: 0 auto;
        padding: 0 20px 40px;
        text-align: left;
        color: #1f2937;
    }

    .checkout-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 18px 0;
    }

    @media screen and (max-width: 640px) {
        .checkout-page {
            padding: 0 16px 32px;
        }

        .checkout-header {
            flex-wrap: wrap;
            padding: 14px 0 12px;
        }

        .header-marks {
            width: 100%;
            justify-content: flex-start;
            gap: 12px;
        }

        .verified-mark,
        .secure-mark {
            font-size: 12px;
        }

        .checkout-progress {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            padding: 8px 0 16px;
        }
    }

    .checkout-logo {
        max-height: 80px;
        max-width: 200px;
        object-fit: contain;
    }

    .header-marks {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .verified-mark,
    .secure-mark {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #6b7280;
        white-space: nowrap;
    }

    .verified-mark {
        color: #12a150;
        font-weight: 600;
    }

    .checkout-progress {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px 0 20px;
    }

    .progress-label {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #6b7280;
        white-space: nowrap;
    }

    .progress-bars {
        display: flex;
        flex: 1;
        gap: 8px;
        min-width: 0;
    }

    .progress-bar {
        flex: 1;
        height: 3px;
        border-radius: 999px;
        background: #d8dce2;
        transition: background-color 0.25s ease;

        &.active {
            background: var(--secondary-color, #2196c9);
        }

        &.done {
            background: #12a150;
        }
    }

    .checkout-body {
        display: grid;
        grid-template-columns: 1fr;
        gap: 24px;
        align-items: start;
    }

    @media screen and (min-width: 1024px) {
        .checkout-body {
            grid-template-columns: minmax(0, 1fr) 340px;
        }

        .checkout-aside-inner {
            position: sticky;
            top: 24px;
        }
    }

    .checkout-main {
        display: flex;
        flex-direction: column;
        gap: 12px;
        min-width: 0;
    }

    .checkout-aside {
        min-width: 0;
    }

    .checkout-footer {
        padding-top: 24px;
        font-size: 13px;
        color: #6b7280;
    }
</style>
